import { useEffect, useRef, Suspense, Component, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoanProvider } from "@/context/LoanContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import { StepContextProvider } from "@/context/StepContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireCalculator, RequireFlag } from "@/components/PageShell";
import { AppShell } from "@/components/AppShell";
// StateSwitcher removed - design-time only tool, not for borrowers
import { EConsentGate } from "@/components/EConsentGate";
import { checkConsentWithApi, getConsentStatus, subscribeConsent } from "@/lib/consent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DbaThemeSync } from "@/components/DbaThemeSync";
import { clearNotificationCache } from "@/lib/clearNotificationCache";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { LO_EMAIL_STORAGE_KEY, loEmailFromSearch } from '@/lib/loEmail';
import { linkReferralFromSearch, storeLinkReferral } from '@/lib/linkReferral';
import { loanNumberFromSearch, storeLinkedLoanNumber } from '@/lib/loanNumberLink';
import { CO_BORROWER_INVITE_PARAM } from '@/lib/coBorrowerInvite';
import { LegalFooterLinks } from "@/components/LegalFooterLinks";

// Lazy-loaded pages — browser only downloads what the user navigates to.
//
// NP-442: routed through `lazyWithReload` rather than bare `React.lazy`. Chunk
// filenames are content-hashed and named only inside the loaded index bundle,
// so a client that was open across a redeploy asks for a hash that no longer
// exists and the import rejects. `lazyWithReload` reloads once (budgeted per
// route, per tab) to pick up the new manifest instead of dead-ending on an
// error page. See src/lib/lazyWithReload.ts.
const Index = lazyWithReload("Index", () => import("./pages/Index"));
const MyLoan = lazyWithReload("MyLoan", () => import("./pages/MyLoan"));
const MyHome = lazyWithReload("MyHome", () => import("./pages/MyHome"));
const Documents = lazyWithReload("Documents", () => import("./pages/Documents"));
const Tasks = lazyWithReload("Tasks", () => import("./pages/Tasks"));
const Conditions = lazyWithReload("Conditions", () => import("./pages/Conditions"));
const Payments = lazyWithReload("Payments", () => import("./pages/Payments"));
// ApplicationRouteGuard includes the redirect logic for submitted applications
const ApplicationRouteGuard = lazyWithReload("ApplicationRouteGuard", () => import("@/components/ApplicationRouteGuard").then(m => ({ default: m.ApplicationRouteGuard })));
const EConsent = lazyWithReload("EConsent", () => import("./pages/EConsent"));
const Income = lazyWithReload("Income", () => import("./pages/Income"));
const Contacts = lazyWithReload("Contacts", () => import("./pages/Contacts"));
const BuyAHome = lazyWithReload("BuyAHome", () => import("./pages/BuyAHome"));
const Refinance = lazyWithReload("Refinance", () => import("./pages/Refinance"));
const DesignSystem = lazyWithReload("DesignSystem", () => import("./pages/DesignSystem"));
const Login = lazyWithReload("Login", () => import("./pages/Login"));
const Register = lazyWithReload("Register", () => import("./pages/Register"));
const ForgotPassword = lazyWithReload("ForgotPassword", () => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithReload("ResetPassword", () => import("./pages/ResetPassword"));
const ResendInvitationLink = lazyWithReload("ResendInvitationLink", () => import("./pages/ResendInvitationLink"));
const ClaimAccount = lazyWithReload("ClaimAccount", () => import("./pages/ClaimAccount"));
const EmailLookup = lazyWithReload("EmailLookup", () => import("./pages/EmailLookup"));
const Settings = lazyWithReload("Settings", () => import("./pages/Settings"));
const NotFound = lazyWithReload("NotFound", () => import("./pages/NotFound"));
const Calculator = lazyWithReload("Calculator", () => import("./pages/Calculator"));
const ReviewApplication = lazyWithReload("ReviewApplication", () => import("./pages/ReviewApplication"));
const ServiceHealth = lazyWithReload("ServiceHealth", () => import("./pages/ServiceHealth"));
const Notifications = lazyWithReload("Notifications", () => import("./pages/Notifications"));
const Sophia = lazyWithReload("Sophia", () => import("./pages/Sophia"));
// NP-582: lazy so the hosted legal text — the Privacy Policy alone is ~32KB — is
// downloaded only by someone who opens a legal page, not by every borrower on load.
const LegalDocument = lazyWithReload("LegalDocument", () => import("./pages/LegalDocument"));

// Preload critical pages after initial render to reduce navigation delay
const preloadPages = () => {
  import("./pages/Index");
  import("./pages/application/ApplicationShell");
  import("./pages/Settings");
  // BUG-DEEP-009 fix: preload Sophia lazy chunks during idle time so rapid navigation
  // away from a page before lazy-load completes doesn't cause ERR_ABORTED failures.
  // (vendor-markdown, SophiaChat, send icon, sophia-api, sophia-avatar all aborted
  //  when Playwright navigated quickly between pages — Sven found 5 net failures.)
  // Issue 3 fix: also preload SophiaChat eagerly on hard refresh (used inside AppShell)
  import("./components/SophiaChat");
  import("./lib/sophia-api");
};
if (typeof window !== 'undefined') {
  // Issue 3 fix: preload Sophia much earlier on hard refresh — start immediately, not at idle.
  // requestIdleCallback fires too late on slow connections; this ensures the Sophia chunks
  // are in the browser cache before AppShell mounts and tries to render SophiaChat.
  import("./components/SophiaChat");
  import("./lib/sophia-api");
  // Also queue the full preload batch at idle for other pages
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(preloadPages);
  } else {
    setTimeout(preloadPages, 2000);
  }
}

/** Loading spinner for lazy pages */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry 401/403/404 — these won't self-resolve
        if (error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403 || status === 404) return false;
        }
        return failureCount < 2;
      },
      // Suppress refetch noise when there's no backend
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Legacy `/newuser` links, still live in borrower inboxes, sent from Link before the route was
 * folded into `/register?otc=`. The otc is the whole reason that email exists — carry the query
 * string across untouched so Register sees the URL it would have been linked with directly.
 * Without one there is no account to finish creating, and the visitor belongs on /login.
 */
export function NewUserRedirect() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasOtc = Boolean(searchParams.get('otc')?.trim());
  return <Navigate to={hasOtc ? `/register${location.search}` : '/login'} replace />;
}

/**
 * Capture ?lo=<email> from URL and persist it (G12).
 * Persistent: once captured, the value survives navigation to URLs without ?lo=.
 * Only overwrites when a new valid email arrives; invalid values are ignored.
 *
 * NP-547: reads through `returnUrl` as well, so a wrapped login link
 * (`/login?returnUrl=/?lo=…`) captures on the login page rather than only after
 * the post-login redirect. Parsing and validation live in loEmailFromSearch, which
 * Register and the DBA theme entry share — three copies of this is what let the
 * wrapped format slip past all of them.
 */
export function LOEmailCapture() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const email = loEmailFromSearch(searchParams.toString());
    if (!email) return;
    localStorage.setItem(LO_EMAIL_STORAGE_KEY, email);
  }, [searchParams]);
  return null;
}

/**
 * Capture a Link invite's realtor referral from the URL (NP-308). Mounted app-wide, like
 * LOEmailCapture, because the Apply Now link lands wherever the loan officer's Apply Now
 * record points and the create it feeds happens pages later, past register or login.
 */
export function LinkReferralCapture() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const referral = linkReferralFromSearch(searchParams.toString());
    if (referral) storeLinkReferral(referral);
  }, [searchParams]);
  return null;
}

/**
 * Capture the loan an email or invite link names (`?loanNumber=`), for LoanContext to open
 * once the borrower's loan list loads. Mounted app-wide because those links land on the
 * sign-in pages, ahead of any loan.
 */
export function LoanNumberLinkCapture() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const loanNumber = loanNumberFromSearch(searchParams.toString());
    if (loanNumber) storeLinkedLoanNumber(loanNumber);
  }, [searchParams]);
  return null;
}

/**
 * BUG-DEEP-010 fix: Show a proper 404 page for completely unknown paths instead of
 * redirecting unauthenticated users to the login screen.
 *
 * The previous `path="*"` catch-all routed all unknown paths through ProtectedRoute,
 * which redirected unauthenticated visitors to /login (even for gibberish URLs like
 * /xyzzy that will never exist). This wrapper checks whether the requested path is
 * a known application route first. Unknown paths render <NotFound> directly without
 * requiring auth; known-but-protected paths continue to go through ProtectedRoute.
 */
const KNOWN_APP_PATHS = [
  '/', '/dashboard', '/e-consent', '/application', '/buy-a-home', '/refinance',
  '/pre-approval', '/my-loan', '/my-home', '/tasks', '/documents', '/conditions', '/payments',
  '/income', '/contacts', '/calculator', '/calculators', '/application/review',
  '/review-application', '/settings', '/design-system', '/profile', '/notifications',
  '/sophia', '/service-health', '/register',
];

function GuardedApp({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isKnown = KNOWN_APP_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  // Unknown paths are publicly accessible 404s — no auth redirect
  if (!isKnown) {
    // NP-582 AC-5 ("any error or fallback page"): this 404 renders outside both
    // AppShell and AuthLayout, so it inherits neither footer. The links go here, not
    // inside NotFound — that component also renders inside AppShell, where the shell
    // footer already supplies them and a second copy would duplicate (AC-8).
    return (
      <div className="min-h-viewport flex flex-col">
        <div className="flex-1">
          <NotFound />
        </div>
        <footer className="px-4 py-6">
          <LegalFooterLinks />
        </footer>
      </div>
    );
  }
  return <>{children}</>;
}

/** Redirect authenticated users away from auth pages.
 *  Respects returnUrl from ProtectedRoute state or SessionTimeout query param
 *  so that post-login redirect lands on the page the user was on before expiry.
 */
// Exported for behavioral tests (login-econsent-gate.test.tsx).
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // E-consent login gate: subscribe to the in-memory consent store so this
  // route re-evaluates the moment GET /api/v1/borrower/me answers.
  const consentUserId = (user?.email ?? user?.id ?? '').toString() || undefined;
  const consentStatus = useSyncExternalStore(
    subscribeConsent,
    () => getConsentStatus(consentUserId),
    () => getConsentStatus(consentUserId),
  );

  // Restored-session kick: EConsentGate lives under the protected catch-all
  // and is NOT mounted on /login or /register, and Login's onSubmit never
  // runs when the loader replaces the form. A persisted session reopened on
  // an auth page would therefore sit on 'unknown' forever — nothing else
  // asks the API. Kick the check here; the ref dedupes re-renders.
  const consentCheckInFlight = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !consentUserId) return;
    if (consentStatus !== 'unknown' || consentCheckInFlight.current) return;
    consentCheckInFlight.current = true;
    void checkConsentWithApi(consentUserId).finally(() => {
      consentCheckInFlight.current = false;
    });
  }, [isAuthenticated, consentUserId, consentStatus]);

  if (isLoading) return null;
  // NP-549 AC-4: the co-borrower's loan attaches on the login POST, so an open session must
  // not be bounced away from `/login?invite=…` — the invitation would never be presented and
  // the loan would never attach, silently, for the 30 days a session lasts. Same reasoning as
  // /email-lookup: a link addressed to one account has to be followable from another.
  //
  // Presence, matching what Login forwards. An unusable value is no longer filtered out here:
  // the API answers one with "nothing attached" rather than a refusal, and AC-10b's notice is
  // what tells the borrower so. `?invite=` with no value stays a bounce — an empty string is
  // falsy, so there is nothing to present.
  const hasCoBorrowerInvite =
    location.pathname === '/login' &&
    Boolean(searchParams.get(CO_BORROWER_INVITE_PARAM));
  // LINK-423/424: the Lead-invite otc reaches either `/register?otc=…` (no PATH account yet)
  // or `/login?otc=…` (an existing account, redeemed on the login POST — see
  // OtcLoanAttachRedeemer in afn-path-api). Both need the same exemption as
  // hasCoBorrowerInvite above: the emailed otc is the credential, so someone already signed
  // in on a shared machine still has to reach the form rather than be bounced to the
  // dashboard before they can even submit it — on `/register` this was formerly handled by
  // `/newuser` being entirely outside PublicOnlyRoute; on `/login` it would otherwise make
  // the existing-user loan-attach otc unreachable whenever a session is already open.
  const hasOtcInvite =
    (location.pathname === '/register' || location.pathname === '/login') &&
    Boolean(searchParams.get('otc')?.trim());
  if (isAuthenticated && !hasCoBorrowerInvite && !hasOtcInvite) {
    // Derive the returnUrl ONCE, the same way Login.tsx does, so the
    // unconsented and consented branches can never drift apart.
    const fromState = (location.state as Record<string, any> | null)?.from;
    const wanted =
      searchParams.get('returnUrl') ||
      (fromState ? (fromState.pathname || '') + (fromState.search || '') : '');

    // E-consent flash fix (per Rock, 2026-07-23): login sets the auth user
    // BEFORE the consent answer arrives, and this redirect used to fire
    // instantly — flashing the Dashboard for a split second before the
    // /e-consent redirect. Hold on the loader while the answer is unknown
    // (the effect above / Login's onSubmit / EConsentGate triggers the API
    // call), then route straight to the correct destination.
    //
    // Degenerate guard: a user object with no email/id can't key the consent
    // store ('unknown' forever) — fall through to the normal redirect and
    // let EConsentGate (the protected-route backstop) own the gate.
    if (consentUserId && consentStatus === 'unknown') return <PageLoader />;
    if (consentUserId && consentStatus === 'unconsented') {
      const eConsentUrl =
        wanted && wanted !== '/'
          ? `/e-consent?returnUrl=${encodeURIComponent(wanted)}`
          : '/e-consent';
      return <Navigate to={eConsentUrl} replace />;
    }
    // NP-213: default to /dashboard so authenticated users are never left at '/'
    return <Navigate to={wanted || '/dashboard'} replace />;
  }
  return <>{children}</>;
}

const AppRoutes = () => (
  <ErrorBoundary>
  <Suspense fallback={<PageLoader />}>
  <LOEmailCapture />
  <LinkReferralCapture />
  <LoanNumberLinkCapture />
  <DbaThemeSync />
  <Routes>
    {/* Auth routes — no shell, public only */}
    <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
    {/* LINK-423/424: also where a borrower invited from Link lands, via `?otc=`. Still
        wrapped in PublicOnlyRoute — the OTC branch inside it (see `hasOtcInvite` below) is
        what exempts THAT case from the authenticated bounce, the same way `/login?invite=`
        already does for the co-borrower claim flow. Plain `/register` (no otc) keeps
        bouncing an already-signed-in visitor to the dashboard, unchanged. */}
    <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
    <Route path="/newuser" element={<NewUserRedirect />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    {/* NP-297: the dead-invitation landing, separate from /forgot-password so the reset
        page Login and ResetPassword link to keeps its own wording. */}
    <Route path="/resend-invitation-link" element={<ResendInvitationLink />} />
    <Route path="/reset-password/:token" element={<ResetPassword />} />
    {/* NP-388: the emailed link is `/reset-password?token=…` — without this
        route it fell through to <NotFound> and no reset could complete. */}
    <Route path="/reset-password" element={<ResetPassword />} />
    {/* NP-549: where the co-borrower's invite email lands. Public — the emailed
        token is the credential, and the account does not exist to log into yet. */}
    <Route path="/claim-account" element={<ClaimAccount />} />
    {/* NP-596: the single landing page every PATH email links to. Public and NOT wrapped
        in PublicOnlyRoute — a borrower who is already signed in still has to be able to
        follow a link addressed to a DIFFERENT address (a co-borrower on a shared
        machine), and bouncing them to the dashboard would strand that person. */}
    <Route path="/email-lookup" element={<EmailLookup />} />
    {/* NP-582 AC-9: PATH's own Terms/Privacy/Disclaimer pages, replacing links out to
        the marketing site. Public and NOT wrapped in PublicOnlyRoute — the login footer
        links here so a signed-out borrower must reach them, and a signed-in one opening
        them in a new tab must not be bounced to the dashboard. */}
    <Route path="/legal/:slug" element={<LegalDocument />} />

    {/* App routes — inside shell, protected */}
    <Route
      path="*"
      element={
        <GuardedApp>
        <ProtectedRoute>
          <AppShell>
            <EConsentGate />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/dashboard" element={<Index />} />
              <Route path="/e-consent" element={<EConsent />} />
              <Route path="/application" element={<ApplicationRouteGuard />} />
              <Route path="/buy-a-home" element={<BuyAHome />} />
              <Route path="/refinance" element={<Refinance />} />
              <Route path="/pre-approval" element={<Navigate to="/application" replace />} />
              <Route path="/my-loan" element={<MyLoan />} />
              <Route path="/my-home" element={<MyHome />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/conditions" element={<Conditions />} />
              {/* NP-754: hidden while incomplete — /payments redirects to /dashboard
                  until the PaymentsPage flag is turned back on. */}
              <Route path="/payments" element={<RequireFlag flag="PaymentsPage"><Payments /></RequireFlag>} />
              <Route path="/income" element={<Income />} />
              <Route path="/contacts" element={<Contacts />} />
              {/* NP-350: all three go through RequireCalculator, which sends them to
                  /dashboard while the DashboardCalculator flag is off (AC-4). */}
              <Route path="/calculator" element={<RequireCalculator><Calculator /></RequireCalculator>} />
              {/* NP-201 Fix 1: /calculators index redirects to the purchase calculator (was 404).
                  With NP-350's flag off that hop lands on /calculators/purchase and is
                  redirected on to /dashboard, so the index needs no flag of its own. */}
              <Route path="/calculators" element={<Navigate to="/calculators/purchase" replace />} />
              <Route path="/calculators/purchase" element={<RequireCalculator><Calculator /></RequireCalculator>} />
              <Route path="/calculators/refinance" element={<RequireCalculator><Calculator /></RequireCalculator>} />
              <Route path="/application/review" element={<Navigate to="/application?step=review" replace />} />
              <Route path="/review-application" element={<ReviewApplication />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/security" element={<Settings />} />
              <Route path="/design-system" element={<DesignSystem />} />
              <Route path="/profile" element={<Navigate to="/settings" replace />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/sophia" element={<Sophia />} />
              <Route path="/service-health" element={<ServiceHealth />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
        </ProtectedRoute>
        </GuardedApp>
      }
    />
  </Routes>
  </Suspense>
  </ErrorBoundary>
);

/**
 * Top-level error boundary rendered above ALL providers.
 * If any provider (AuthProvider, LoanProvider, FeatureFlagProvider, etc.) crashes
 * during render the ErrorBoundary inside AppRoutes cannot catch it — React unmounts
 * the entire tree resulting in a blank white page.  This outer boundary is the
 * last-resort safety net: it shows a visible error instead of a white page.
 *
 * P0 WHITE-PAGE FIX: any uncaught render error now surfaces as a recovery UI
 * instead of silently unmounting the React tree.
 */
class OuterErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[OuterErrorBoundary] Provider-level crash caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.error
        ? `[${this.state.error.name}] ${this.state.error.message}`
        : 'Application failed to load. Please refresh the page.';
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                      minHeight:'100vh', padding:'2rem', fontFamily:'sans-serif', textAlign:'center' }}>
          <h1 style={{ fontSize:'1.5rem', marginBottom:'1rem', color:'#ef4444' }}>Something went wrong</h1>
          <p style={{ color:'#6b7280', marginBottom:'1.5rem', maxWidth:'480px' }}>{msg}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ padding:'0.5rem 1.5rem', background:'#3b82f6', color:'#fff',
                     border:'none', borderRadius:'0.375rem', cursor:'pointer', fontSize:'1rem' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  // Clear notification cache on startup to prevent stale data
  useEffect(() => {
    clearNotificationCache();
  }, []);

  return (
    <OuterErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* RCA-fix (Step1-Regression-v2): FeatureFlagProvider moved ABOVE BrowserRouter.
            When it was inside BrowserRouter, NavigationRefresher/RouterLocationWatcher
            subscribed to useLocation() and triggered setFlags() on every pathname change.
            That extra setFlags() call — firing asynchronously while handleSaveAndContinue
            was running API calls — caused FeatureFlagContext.Provider to re-render with a
            new context value. In React 18's concurrent-mode scheduling, this re-render
            could interleave with the async step-advance code and (in specific timing
            windows) cause ApplicationShell to skip the syncUrlStep() navigate() call.
            Moving FeatureFlagProvider above BrowserRouter means useInRouterContext()
            returns false inside NavigationRefresher, so it renders null — the per-route
            flag refresh is disabled. Flags are still fetched on mount + every 5 minutes. */}
        <FeatureFlagProvider>
          <BrowserRouter>
            <AuthProvider>
              <LoanProvider>
                <StepContextProvider>
                  <AppRoutes />
                </StepContextProvider>
              </LoanProvider>
            </AuthProvider>
          </BrowserRouter>
        </FeatureFlagProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </OuterErrorBoundary>
  );
};

export default App;
